import NProgress from "nprogress";
import * as Sentry from "@sentry/react";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import ErrorBoundary from "../src/ErrorBoundary";
import { appWithTranslation } from "next-i18next";
import { SettingsProvider } from "@hooks/settings";
import AuthLayout from "../src/layouts/AuthLayout";
import GuestLayout from "../src/layouts/GuestLayout";
import { GoogleTagManager } from "@next/third-parties/google";
import { SessionProvider, useSession } from "next-auth/react";

import "../styles/nprogress.css";
import "../styles/scss/dentricedev.scss";
import "react-toastify/dist/ReactToastify.css";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "../styles/index.css";

// for reporting web vitals whenever needed
// export function reportWebVitals(metric) {
//   console.log(metric);
// }

// function MyApp({ Component, pageProps }) {
//   const [open, setOpen] = React.useState(false);
//   const router = useRouter();
//   const showMobilemenu = () => {
//     setOpen(!open);
//   };

//   useEffect(() => {
//     const bootstrap = require("bootstrap");
//     window.bootstrap = bootstrap;
//     if (open) {
//       document.body.classList.add("g-sidenav-pinned");
//     } else {
//       document.body.classList.remove("g-sidenav-pinned");
//     }

//     if (document.body.classList.contains("g-sidenav-pinned")) {
//       // attach an event listener to document, when clicked remove g-sidenav-pinned class
//       document.body.addEventListener("click", function (event) {
//         if (
//           !event.target.closest(".sidenav-toggler") &&
//           !event.target.closest(".sidenav-toggler-inner") &&
//           !event.target.closest("#sidenav-main")
//         ) {
//           setOpen(!open);
//           document.body.classList.remove("g-sidenav-pinned");
//         }
//       });

//       // attach an event listener to document when keydown ESC remove g-sidenav-pinned class
//       document.addEventListener("keydown", function (event) {
//         if (event.key === "Escape") {
//           setOpen(!open);
//           document.body.classList.remove("g-sidenav-pinned");
//         }
//       });
//     }

//     router.events.on("routeChangeStart", () => NProgress.start());

//     router.events.on("routeChangeComplete", () => NProgress.done());
//     router.events.on("routeChangeError", () => NProgress.done());
//   }, [open, router.events]);

//   return (
//     <Sentry.ErrorBoundary fallback={ErrorBoundary} showDialog>
//       <SessionProvider session={pageProps.session}>
//         <SettingsProvider>
//           {Component.auth ? (
//             <Auth>
//               <AuthLayout
//                 showMobilemenu={() => showMobilemenu()}
//                 hideFooter={Component.hideFooter}
//                 hideTopbar={Component.hideTopbar}
//               >
//                 <Component {...pageProps} />
//               </AuthLayout>
//             </Auth>
//           ) : (
//             <GuestLayout>
//               <Component {...pageProps} />
//             </GuestLayout>
//           )}

//           {process.env.NODE_ENV === "production" && (
//             <GoogleTagManager gtmId="GTM-T2NK3X96" />
//           )}
//         </SettingsProvider>
//       </SessionProvider>
//     </Sentry.ErrorBoundary>
//   );
// }

function MyApp({ Component, pageProps }) {
  const [open, setOpen] = React.useState(false);
  const router = useRouter();
  const showMobilemenu = () => {
    setOpen(!open);
  };

  if (Component.skipDocumentJsContent) {
    pageProps.skipDocumentJsContent = true;
  }

  useEffect(() => {
    const bootstrap = require("bootstrap");
    window.bootstrap = bootstrap;
    if (open) {
      document.body.classList.add("g-sidenav-pinned");
    } else {
      document.body.classList.remove("g-sidenav-pinned");
    }

    if (document.body.classList.contains("g-sidenav-pinned")) {
      document.body.addEventListener("click", function (event) {
        if (
          !event.target.closest(".sidenav-toggler") &&
          !event.target.closest(".sidenav-toggler-inner") &&
          !event.target.closest("#sidenav-main")
        ) {
          setOpen(!open);
          document.body.classList.remove("g-sidenav-pinned");
        }
      });

      document.addEventListener("keydown", function (event) {
        if (event.key === "Escape") {
          setOpen(!open);
          document.body.classList.remove("g-sidenav-pinned");
        }
      });
    }

  }, [open]);

  // Recover from stale chunks after a deploy: when a client-side navigation
  // fails because a code-split chunk no longer exists (the browser is still
  // running old HTML), reload once to pull fresh HTML + assets instead of
  // silently hanging on a loading state. Registered in its own effect with
  // cleanup so the handlers aren't duplicated on every sidebar toggle.
  useEffect(() => {
    const isStaleChunkError = (err) => {
      const message = err?.message || String(err || "");
      return (
        err?.name === "ChunkLoadError" ||
        /Loading chunk [\w-]+ failed/i.test(message) ||
        /Loading CSS chunk/i.test(message) ||
        /ChunkLoadError/i.test(message)
      );
    };

    const reloadForFreshAssets = (targetUrl) => {
      // Guard against reload loops: at most one auto-reload per 10s.
      const now = Date.now();
      const last = Number(sessionStorage.getItem("chunk_reloaded_at") || 0);
      if (now - last < 10000) return;
      sessionStorage.setItem("chunk_reloaded_at", String(now));
      window.location.href = targetUrl || window.location.href;
    };

    const handleStart = () => NProgress.start();
    const handleComplete = () => NProgress.done();
    // Failed client-side navigation (old HTML still loaded after a deploy).
    const handleError = (err, route) => {
      NProgress.done();
      if (isStaleChunkError(err)) reloadForFreshAssets(route);
    };
    // Dynamic imports (e.g. react-apexcharts on the dashboard) can fail after a
    // deploy outside of a route change — those surface as window errors or
    // unhandled rejections rather than routeChangeError.
    const handleWindowError = (event) => {
      if (isStaleChunkError(event?.error || event?.message)) {
        reloadForFreshAssets();
      }
    };
    const handleUnhandledRejection = (event) => {
      if (isStaleChunkError(event?.reason)) reloadForFreshAssets();
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleError);
    window.addEventListener("error", handleWindowError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleError);
      window.removeEventListener("error", handleWindowError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, [router.events]);

  const isEmbed = router.query.embed === "1";

  if (Component.skipAppWrappers) {
    return <Component {...pageProps} />;
  }

  // 👇 Allow pages to override layout
  const getLayout =
    Component.getLayout ||
    ((page) =>
      Component.auth ? (
        <Auth>
          <AuthLayout
            showMobilemenu={() => showMobilemenu()}
            hideFooter={Component.hideFooter}
            hideTopbar={Component.hideTopbar}
            noContainer={Component.noContainer}
            embed={isEmbed}
          >
            {page}
          </AuthLayout>
        </Auth>
      ) : (
        <GuestLayout>{page}</GuestLayout>
      ));

  return (
    <Sentry.ErrorBoundary fallback={ErrorBoundary} showDialog>
      <SessionProvider session={pageProps.session}>
        <SettingsProvider>
          {getLayout(<Component {...pageProps} />)}
          {process.env.NODE_ENV === "production" && (
            <GoogleTagManager gtmId="GTM-T2NK3X96" />
          )}
        </SettingsProvider>
      </SessionProvider>
    </Sentry.ErrorBoundary>
  );
}

function Auth({ children }) {
  // if `{ required: true }` is supplied, `status` can only be "loading" or "authenticated"
  const { status, data: session } = useSession({ required: true });
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);

  useEffect(() => {
    if (session?.user) {
      Sentry.setUser({
        id: session.user.subscriberId,
        email: session.user.email,
        username: session.user.name,
      });
    }
  }, [session]);

  // Safety net: useSession({ required: true }) normally resolves to
  // "authenticated" or redirects to sign-in, but if /api/auth/session never
  // settles (stale cookie, network/NextAuth hiccup) `status` can stay "loading"
  // indefinitely, trapping the user on the purple preloader. After a generous
  // timeout, offer a reload instead of hanging forever. This does NOT grant
  // access or change any auth logic — it only replaces the infinite spinner.
  useEffect(() => {
    if (status !== "loading") {
      setLoadingTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setLoadingTimedOut(true), 25000);
    return () => clearTimeout(timer);
  }, [status]);

  if (status === "loading") {
    return (
      <div className="row m-auto">
        <div
          className="col-12 min-vh-100"
          style={{
            backgroundColor: "var(--bs-bota-purple)",
          }}
        >
          <div className="row justify-content-center">
            <div id="preloader1"></div>
          </div>
          {loadingTimedOut && (
            <div className="row justify-content-center">
              <div
                className="col-auto text-center text-white"
                style={{ marginTop: 24 }}
              >
                <p style={{ marginBottom: 12 }}>
                  Está demorando mais que o normal para carregar sua sessão.
                </p>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => window.location.reload()}
                >
                  Recarregar página
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return children;
}

export default appWithTranslation(MyApp);
